import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, RefreshCw, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '@helios/ui';
import type { CompanySetupView } from '@helios/types';
import { HeliosChatPanel } from '@/components/layout/AiAdvisorDrawer';
import { type HeliosSetupChatContext } from '@/components/layout/HeliosChatWindow';
import { useAuth } from '@/features/auth/authContext';
import { companySetupClient, type SetupDraft } from '@/services/companySetupClient';
import { getSessionOriginKey, isCurrentSessionOrigin } from '@/services/sessionOrigin';
import { useUiStore } from '@/stores/uiStore';
import { SetupCheck } from './SetupControls';

export interface SetupAssistantProps {
  view: CompanySetupView;
  draft: SetupDraft;
  dirty: boolean;
  busy: boolean;
  stepLabel?: string;
  onUpdate: (patch: Partial<SetupDraft>) => void;
  onRun: (work: () => Promise<void>) => Promise<void>;
  onRefresh: () => Promise<void>;
  onAcceptView: (view: CompanySetupView) => void;
}

/** Adapter for the shared Helios AI window; all mutations retain setup API governance. */
export function SetupAssistant({
  view,
  draft,
  dirty,
  busy,
  stepLabel,
  onUpdate,
  onRun,
  onRefresh,
  onAcceptView,
}: SetupAssistantProps) {
  const auth = useAuth();
  const setOpen = useUiStore((state) => state.setAdvisorOpen);
  const [messages, setMessages] = useState<HeliosSetupChatContext['messages']>([]);
  const [chatError, setChatError] = useState<string | null>(null);
  const mounted = useRef(true);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const active = view.status === 'ACTIVE';
  const scopeKey = JSON.stringify([
    auth.user?.id,
    auth.tenant?.id,
    auth.sessionId,
    auth.authVersion,
    view.id,
  ]);
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    setMessages([]);
    setChatError(null);
  }, [scopeKey]);
  const disabledReason = active
    ? 'This company is active. Enter the platform to continue with Helios AI.'
    : busy
      ? 'Wait for the current setup action to finish.'
      : dirty
        ? 'Save your changes before asking Helios AI.'
        : !view.aiConsent || !draft.aiConsent
          ? 'Allow AI assistance in the setup sidebar, then save your progress.'
          : view.aiRequestsRemaining === 0
            ? 'Your setup AI allowance is used. You can continue with the forms.'
            : null;
  const ask = async (message: string) => {
    if (disabledReason || message.trim().length < 2 || message.length > 2000) return false;
    let sent = false;
    setChatError(null);
    const origin = getSessionOriginKey();
    const requestScope = scopeKey;
    await onRun(async () => {
      let result: Awaited<ReturnType<typeof companySetupClient.ask>>;
      try {
        result = await companySetupClient.ask(view.id, view.version, message);
      } catch (error) {
        if (
          mounted.current &&
          currentScope.current === requestScope &&
          isCurrentSessionOrigin(origin)
        ) {
          setChatError(
            error instanceof Error
              ? error.message
              : 'Unable to send your setup question. Try again.',
          );
        }
        throw error;
      }
      if (
        !mounted.current ||
        currentScope.current !== requestScope ||
        !isCurrentSessionOrigin(origin)
      )
        return;
      const id = crypto.randomUUID();
      setMessages((current) => [
        ...current,
        { id: `${id}:question`, role: 'USER', content: message },
        { id: `${id}:reply`, role: 'ASSISTANT', content: result.reply },
      ]);
      sent = true;
      await onRefresh();
    });
    return sent;
  };
  return (
    <aside className="company-setup-assistant">
      <div className="company-setup-assistant-emblem" aria-hidden="true">
        <Sparkles size={24} />
      </div>
      <span className="company-setup-eyebrow">WITH YOU AT EVERY STEP</span>
      <h2>Helios AI</h2>
      <p>
        Your platform assistant, right here. Get help with company settings and access decisions.
      </p>
      <Button
        ref={launcherRef}
        aria-label="Open Helios AI"
        onClick={() => setOpen(true)}
        className="company-setup-ai-launch"
      >
        <Sparkles size={16} /> Ask Helios AI <ArrowUpRight size={15} />
      </Button>
      <SetupCheck
        checked={draft.aiConsent}
        disabled={active || busy}
        onChange={(value) => onUpdate({ aiConsent: value })}
      >
        Allow my question and company settings to be sent to the configured AI provider. Keep
        secrets and employee details out of chat.
      </SetupCheck>
      <small className="company-setup-ai-allowance">
        {view.aiRequestsRemaining} setup requests remaining; no paid overages.
      </small>
      {dirty ? (
        <small>Save changes before asking. Your forms stay available at every step.</small>
      ) : null}
      <div className="company-setup-assistant-rule">
        <ShieldCheck size={15} />
        <small>You review every proposed change.</small>
      </div>
      {view.proposals
        .filter((proposal) => proposal.status === 'PENDING')
        .map((proposal) => (
          <section className="company-setup-proposal" key={proposal.id}>
            <h3>Review proposed change</h3>
            <p>{proposal.summary}</p>
            <details>
              <summary>Exact company settings</summary>
              <pre>{JSON.stringify(proposal.args, null, 2)}</pre>
            </details>
            <div>
              <Button
                disabled={busy || active || dirty || proposal.baseVersion !== view.version}
                onClick={() =>
                  void onRun(async () =>
                    onAcceptView(
                      await companySetupClient.decide(view.id, proposal.id, view.version, 'APPLY'),
                    ),
                  )
                }
              >
                Apply to draft
              </Button>
              <Button
                variant="outline"
                disabled={busy || active || dirty}
                onClick={() =>
                  void onRun(async () =>
                    onAcceptView(
                      await companySetupClient.decide(view.id, proposal.id, view.version, 'REJECT'),
                    ),
                  )
                }
              >
                Reject
              </Button>
            </div>
            {dirty ? (
              <small>Save your form changes before applying or rejecting a proposal.</small>
            ) : null}
            {proposal.baseVersion !== view.version ? (
              <small>This proposal is stale. Reject it and request a new one.</small>
            ) : null}
          </section>
        ))}
      <details className="company-setup-history">
        <summary>Setup history</summary>
        {view.events.length === 0 ? (
          <p>No setup history recorded yet.</p>
        ) : (
          view.events.map((event) => (
            <p key={event.id}>
              <strong>{event.action.replaceAll('.', ' ')}</strong>
              <small>{new Date(event.createdAt).toLocaleString()}</small>
            </p>
          ))
        )}
      </details>
      <Button variant="ghost" disabled={busy || dirty} onClick={() => void onRefresh()}>
        <RefreshCw size={14} /> Refresh saved state
      </Button>
      <HeliosChatPanel
        setup={{
          scopeKey,
          companyName: draft.profile.workspaceName || draft.profile.legalName,
          stepLabel,
          messages,
          busy,
          disabledReason,
          onSend: ask,
          onReturnFocus: () => launcherRef.current?.focus(),
          footer: (
            <>
              {chatError ? (
                <p role="alert" className="text-critical mt-2 text-xs">
                  {chatError}
                </p>
              ) : null}
              <p className="text-muted-foreground mt-2 text-[11px] leading-relaxed">
                {view.aiRequestsRemaining} setup requests remaining. Keep secrets and employee
                details out of chat. Close this window to review proposed changes.
              </p>
            </>
          ),
        }}
      />
    </aside>
  );
}
